import { type ComponentProps } from 'react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ScrollView, VStack } from 'native-base';

import { SortableItem } from './sortable-item.web';

type SortableListProps<TItem> = ComponentProps<typeof ScrollView> &
  Pick<ComponentProps<typeof DndContext>, 'sensors'> & {
    _contentBodyProps?: ComponentProps<typeof VStack>;
    data?: TItem[];
    headerComponent?: React.ReactElement | React.ReactNode;
    keyExtractor: (item: TItem) => string;
    onChange?: (_data: TItem[]) => void;
    renderItem: (item: TItem) => React.ReactElement | React.ReactNode;
  };

export const SortableList = <TItem extends {}>({
  _contentBodyProps,
  data = [],
  sensors,
  headerComponent,
  keyExtractor,
  renderItem,
  onChange,
  ..._scrollViewProps
}: SortableListProps<TItem>) => {
  const defaultSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 500, tolerance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 500, tolerance: 5 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const idStr = (id: unknown) => String(id ?? '');
    const oldIndex = data.findIndex(m => keyExtractor(m) === idStr(active.id));
    const newIndex = data.findIndex(m => keyExtractor(m) === idStr(over.id));

    if (oldIndex === -1 || newIndex === -1) onChange?.(data);
    else onChange?.(arrayMove(data, oldIndex, newIndex));
  };

  return (
    <ScrollView flex={1} stickyHeaderIndices={[0]} showsVerticalScrollIndicator={false} {..._scrollViewProps}>
      {!!headerComponent && headerComponent}
      <DndContext sensors={sensors ?? defaultSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={data.map(keyExtractor)} strategy={verticalListSortingStrategy}>
          <VStack {..._contentBodyProps}>
            {data.map((_item, index) => {
              const elemId = keyExtractor(_item);
              return (
                <SortableItem key={elemId ?? `sortable-list-dnd.${index}`} id={elemId}>
                  {renderItem(_item)}
                </SortableItem>
              );
            })}
          </VStack>
        </SortableContext>
      </DndContext>
    </ScrollView>
  );
};
